import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import React, { useEffect, useMemo, useState } from "react";
import moment from 'moment';
import { useDispatch, useSelector } from "react-redux";
import { Spin, Tooltip } from 'antd';
import { ceil } from 'lodash';
import { AiOutlineDownload } from "react-icons/ai";

import AgGridReact from "../../components/ag-grid-table/index.js";
import Pagination from '../../components/pagination';
import Badge from '../../components/uiComponent/bages';

import { getAllFileUploadHistory } from "../../slices/po.js";

import { convertUtcToEst } from '../../utils/helpers.js';

let gridColumnApi

const FileUploadHistory = () => {

  const dispatch = useDispatch();
  const { fetchingAllFileUploadHistory } = useSelector((state) => state.pos);

  const [fileUploadHistory, setFileUploadHistory] = useState([]);
  const [page, setPage] = useState({ pageSize: 25, pageNumber: 1 });
  const [sortBy, setSortBy] = useState({ key: '', type: '' });


  const skusColDef = useMemo(() => [
    {
      headerName: 'File Name',
      field: 'file_name',
      sortable: true,
      comparator: () => '',
      width: 350,
      cellRenderer: ({ data }) => {
        const { file_name, orig_file_url } = data || {};
        const isDisabled = !orig_file_url;

        return isDisabled ? (
          <Tooltip title={file_name}>
            <span style={{ cursor: 'not-allowed' }}>{file_name}</span>
          </Tooltip>
        ) : (
          <Tooltip title={`Download ${file_name}`}>
            <a
              href={orig_file_url}
              download
              style={{ color: '#1677ff', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {file_name}
            </a>
          </Tooltip>
        );
      },
    },
    { headerName: 'File Type', field: 'file_type' },
    {
      headerName: 'Status',
      width: 150,
      field: 'status',
      cellRenderer: ({ value }) => <Badge status={value === 'inProgress' ? 'in progress' : 'completed'} />,
    },
    {
      headerName: 'Total',
      field: 'total_records',
      sortable: true,
      width: 150,
      comparator: () => 0,
      cellRenderer: ({ data }) =>
        data.status === 'inProgress' ? '-' : (data.total_records || 0)
    },
    {
      headerName: 'Success',
      field: 'success_records',
      sortable: true,
      width: 150,
      comparator: () => 0,
      cellRenderer: ({ data }) =>
        data.status === 'inProgress' ? '-' : (data.success_records || 0),
    },
    {
      headerName: 'Failure',
      field: 'error_records',
      sortable: true,
      width: 150,
      comparator: () => 0,
      cellRenderer: ({ data }) =>
        data.status === 'inProgress' ? '-' : (data.error_records || 0),
    },
    {
      headerName: 'Created Date',
      field: 'created_at',
      sortable: true,
      comparator: () => 0,
      cellRenderer: ({ value }) => {
        const date = new Date(value);
        return convertUtcToEst(date,{
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      }
    },
    {
      headerName: 'Action',
      field: 'file_url',
      cellRenderer: ({ data }) => {
        const { file_url, status } = data || {};
        const isDisabled = status !== 'completed' || !file_url;

        return isDisabled ? (
          <Tooltip title='No Errors in this File'>
            <AiOutlineDownload size={25} style={{ fontSize: 18, color: '#ccc', cursor: 'not-allowed' }} />
          </Tooltip>
        ) : (
          <a href={file_url} target="_blank" rel="noopener noreferrer">
            <Tooltip title='Download Error File'>
              <AiOutlineDownload size={25} style={{ fontSize: 18 }} />
            </Tooltip>
          </a>
        );
      },
    }
  ]);

  const gridReady = (params) => {
    gridColumnApi = params.columnApi
  }

  const { pageNumber, pageSize } = page || {};
  const { total: totalCount } = fileUploadHistory?.pagination || {}


  const handlePagination = (number, key) => {
    const value = Number(number);
    let newPage = {};

    if (key === 'pageSize') newPage = { pageNumber: 1, pageSize: value };
    else newPage = { ...page, pageNumber: value };

    setPage(newPage);

    dispatch(getAllFileUploadHistory({
      page: newPage,
      limit: pageSize,
      sortBy
    })).then((res) => {
      if (res?.payload?.success && res?.payload?.data) {
        setFileUploadHistory(res?.payload);
      } else {
        setFileUploadHistory([]);
      }
    }
    );
  };

  const refreshGridClick = () => {
    if (gridColumnApi){
      gridColumnApi.applyColumnState({
        defaultState: { sort: null },
        applyOrder: true,
      });
      setSortBy({ key: '', type: '' })
    }
    setSortBy({ key: '', type: '' });
    dispatch(getAllFileUploadHistory({
      page: page.pageNumber,
      limit: pageSize,
      sortBy: { key: '', type: '' }
    })).then((res) => {
      if (res?.payload?.success && res?.payload?.data) {
        setFileUploadHistory(res?.payload);
      } else {
        setFileUploadHistory([]);
      }
    }
    );
  };


  useEffect(() => {
    dispatch(getAllFileUploadHistory()).then((res) => {
      if (res?.payload?.success && res?.payload?.data) {
        setFileUploadHistory(res?.payload);
      } else {
        setFileUploadHistory([]);
      }
    }
    )
  }, []);
  const gridOptions = {
    resizable: true,
    sortable: false,
    suppressMenu: true,
    suppressMovable: true,
    suppressSorting: true,
  };

  const onSortChange = (params) => {
    const value = params.columnApi.getColumnState().find(s => s.sort != null)

    if (value && ['file_name', 'file_url', 'orig_file_url', 'total_records', 'success_records', 'error_records', 'created_at'].includes(value?.colId)) {
      const { colId, sort } = value || {};
      setSortBy({ key: colId, type: sort });
      dispatch(getAllFileUploadHistory({
        page: page.pageNumber,
        limit: pageSize,
        sortBy: { key: colId, type: sort }

      })).then((res) => {
        if (res?.payload?.success && res?.payload?.data) {
          setFileUploadHistory(res?.payload);
        } else {
          setFileUploadHistory([]);
        }
      }
      );
    }
  }

  return (
    <div>
      <Spin spinning={fetchingAllFileUploadHistory}>
        <AgGridReact
          gridReady={gridReady}
          user_referrals={fileUploadHistory?.data}
          rowHeight={57}
          height={"125px"}
          columnDefinitions={skusColDef}
          rowSelection='multiple'
          defaultColDef={gridOptions}
          onSortChange={onSortChange}
          className="sku-table"
        />
        <Pagination
          total={totalCount}
          totalPages={ceil(totalCount / pageSize + 1)}
          pageNumber={pageNumber}
          pageLimit={pageSize}
          handleLimitChange={(e) => handlePagination(e, 'pageSize')}
          handlePageNumberChange={(e) => handlePagination(e, 'pageNumber')}
          handlePageChangeLeft={(e) => handlePagination(e, 'pageNumber')}
          handlePageChangeRight={(e) => handlePagination(e, 'pageNumber')}
          refreshGridClick={refreshGridClick}
        />
      </Spin>
    </div>
  );
}

export default FileUploadHistory;